export default function PolynomialFitExplanation() {
  return (
    <div className="w-full p-1 text-xs text-gray-800 text-justify leading-relaxed hyphens-auto md:text-sm">
      <p>
        <strong>Goal:</strong> Approximate a dataset with a polynomial that does <em>not necessarily</em> pass through all data points,
        but minimizes the total squared vertical error. 
        This is known as a least squares fit and is widely used for trend detection and data smoothing.
        <br></br><br></br>It is a powerful tool in data analysis, especially when the underlying model is unknown but smooth trends are expected.
      </p>

      <p className="mt-3"><strong>Mathematical explanation:</strong></p>
      <ul className="list-disc pl-5 space-y-2 mb-4">
        <li>
          Given data points <code>(xᵢ, yᵢ)</code> for <code>i = 0,...,n−1</code> and a desired polynomial degree <code>d</code>,
          the algorithm finds coefficients that minimize the squared error:
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1 flex items-center justify-center text-center min-h-[40px]">
            ∑ᵢ (p(xᵢ) − yᵢ)²
          </pre>
        </li>
        <li>
          This leads to an <em>overdetermined</em> linear system (more equations than unknowns),
          which is solved using the <strong>normal equations</strong>.
        </li>
        <li>
          The resulting polynomial has the form:
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs mb-1 flex items-center justify-center text-center min-h-[40px]">
            p(x) = a₀ + a₁·x + a₂·x² + … + a<sub>d</sub>·xᵈ
          </pre>
        </li>
        <li>
          For a meaningful fit, the number of data points <code>n</code> should exceed the degree <code>d</code> by a reasonable margin.
          If you choose <br></br> <code>d ≥ n−1</code>, the polynomial may interpolate the points exactly, which is not the goal here.
        </li>
        <li>
          Here, only polynomials of degree ≤ 5 are available. Very high degrees can lead to numerical instability or oscillations (Runge’s phenomenon).
        </li>
      </ul>

      <p className="mt-3"><strong>Input format:</strong></p>
      <ul className="list-disc pl-5 space-y-1 mb-2">
        <li>Enter points as an 2D-array of <code>[x, y]</code> pairs:</li>
        <pre className="bg-white p-2 border border-gray-200 rounded text-xs mt-1">
[[0, 1], [1, 2], [2, 1.5], 
<br /> [3, 4.0], [6, 2]]
        </pre>
      </ul>

      <p className="mt-3"><strong>Output format:</strong></p>
      <ul className="list-disc pl-5 space-y-2 mb-4">
        <li>The fitted curve is plotted alongside the original data points.</li>
        <li>Error metrics are shown: average and maximum vertical deviation between the fit and the points.</li>
        <li>The full coefficient vector <code>[a₀, a₁, ..., a<sub>d</sub>]</code> is displayed for analysis or export.</li>
      </ul>


    </div>
  );
}
